import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { track } from '@/lib/track';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  FolderLock,
  Lock,
  Download,
  Eye,
  FileText,
  Shield,
  Loader2,
  PackageOpen,
  AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const CAT_LABEL = {
  pitch_deck: 'Pitch Deck / Overview',
  preclinical: 'Preclinical',
  clinical: 'Clinical',
  cmc: 'CMC / Manufacturing',
  regulatory: 'Regulatory',
  ip: 'IP / Patents',
  financials: 'Financials',
  team: 'Team & Corporate',
  market: 'Market / Commercial',
  misc: 'Misc / Other',
};

function fmtBytes(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export default function PublicDataRoomPage() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [unlocked, setUnlocked] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState('');

  useEffect(() => {
    axios
      .get(`${API}/public/data-room/${token}/meta`)
      .then((r) => setMeta(r.data))
      .catch((e) => {
        const code = e?.response?.status;
        setMetaError(code === 404 ? 'Invalid link' : 'Could not load Data Room');
      });
  }, [token]);

  const handleUnlock = async (e) => {
    e?.preventDefault?.();
    setUnlockError('');
    setUnlocking(true);
    try {
      const res = await axios.post(`${API}/public/data-room/${token}/unlock`, {
        password,
        email: email.trim(),
        nda_accepted: ndaAccepted,
      });
      setUnlocked(res.data);
      track('data_room_unlocked', `/dataroom/${token}`, {});
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) setUnlockError('Incorrect password.');
      else if (status === 403) setUnlockError(typeof detail === 'string' ? detail : 'Your email is not authorized for this Data Room.');
      else if (status === 410) setUnlockError(typeof detail === 'string' ? detail : 'This link has been revoked or has expired.');
      else if (status === 428) setUnlockError('Please accept the confidentiality notice to continue.');
      else setUnlockError('Could not unlock. Please retry.');
    } finally {
      setUnlocking(false);
    }
  };

  if (metaError) {
    return (
      <Centered>
        <div className="text-rose-600 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span>{metaError}</span>
        </div>
      </Centered>
    );
  }

  if (!meta) {
    return (
      <Centered>
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </Centered>
    );
  }

  if (meta.is_revoked) {
    return (
      <Centered>
        <div className="max-w-md text-center">
          <FolderLock className="w-10 h-10 mx-auto text-slate-400 mb-3" />
          <div className="text-lg font-semibold text-slate-800">Access revoked</div>
          <div className="text-sm text-slate-500 mt-2">
            This Data Room link has been deactivated by {meta.biotech_name}. Please contact them
            directly if you require continued access.
          </div>
        </div>
      </Centered>
    );
  }

  if (meta.is_expired) {
    return (
      <Centered>
        <div className="max-w-md text-center">
          <FolderLock className="w-10 h-10 mx-auto text-rose-400 mb-3" />
          <div className="text-lg font-semibold text-slate-800">This link has expired</div>
          <div className="text-sm text-slate-500 mt-2">
            The Data Room link for {meta.biotech_name} expired on {new Date(meta.expires_at).toLocaleDateString()}.
            Please contact them for a fresh link.
          </div>
        </div>
      </Centered>
    );
  }

  if (!unlocked) {
    return (
      <Centered>
        <form onSubmit={handleUnlock} className="w-full max-w-md bg-white border border-slate-200 rounded-xl p-7 shadow-sm">
          <div className="flex items-center gap-2 text-amber-700 mb-1">
            <FolderLock className="w-5 h-5" />
            <span className="text-xs uppercase tracking-wider font-semibold">Confidential Data Room</span>
          </div>
          <h1 className="text-xl font-semibold text-slate-900">{meta.biotech_name}</h1>
          {meta.asset_name && (
            <div className="text-sm text-amber-700 mt-1 font-medium">
              Asset: {meta.asset_name}
            </div>
          )}
          <div className="text-sm text-slate-500 mt-1">
            Shared with <span className="font-medium text-slate-700">{meta.recipient_pharma_name}</span>
          </div>

          <div className="mt-6">
            <label className="text-xs text-slate-600 flex items-center gap-1 mb-1">
              <Lock className="w-3 h-3" /> Password
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter access password"
              autoFocus
              data-testid="public-dr-password"
            />
          </div>

          {meta.nda_required && (
            <label className="mt-4 flex items-start gap-2 cursor-pointer" data-testid="public-dr-nda">
              <input
                type="checkbox"
                checked={ndaAccepted}
                onChange={(e) => setNdaAccepted(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-[12px] text-slate-600 leading-snug">
                I acknowledge that the information in this Data Room is <strong>confidential</strong> and
                will be used solely for the purpose of my organization&apos;s evaluation.
              </span>
            </label>
          )}

          {unlockError && <div className="text-xs text-rose-600 mt-3">{unlockError}</div>}

          <Button
            type="submit"
            disabled={unlocking || !password || (meta.nda_required && !ndaAccepted)}
            className="w-full mt-4"
            data-testid="public-dr-unlock-btn"
          >
            {unlocking ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Unlocking…
              </>
            ) : (
              <>Unlock Data Room</>
            )}
          </Button>

          {meta.expires_at && !meta.is_expired && (
            <div className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
              This link expires on {new Date(meta.expires_at).toLocaleDateString()}.
            </div>
          )}

          <div className="mt-5 text-[11px] text-slate-400 flex items-start gap-1">
            <Shield className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>
              All access is logged. Files are confidential and intended solely for evaluation by{' '}
              {meta.recipient_pharma_name}.
            </span>
          </div>
        </form>
      </Centered>
    );
  }

  return <UnlockedView token={token} password={password} email={email} data={unlocked} />;
}

function UnlockedView({ token, password, email, data }) {
  const { biotech_name, recipient_pharma_name, asset_name, files } = data;
  const downloadPermission = data.download_permission || 'preview_download';
  const canDownloadIndividual = downloadPermission === 'preview_download' || downloadPermission === 'download_all';
  const canDownloadZip = downloadPermission === 'download_all' || downloadPermission === 'preview_download';
  const showDownloadDisabledNotice = downloadPermission === 'preview_only' || downloadPermission === 'disabled';

  const grouped = useMemo(() => {
    const out = {};
    for (const f of files) {
      const key = CAT_LABEL[f.data_room_category] || 'Misc / Other';
      if (!out[key]) out[key] = [];
      out[key].push(f);
    }
    return out;
  }, [files]);

  const handleDownload = (fileId, name) => {
    const url = `${API}/public/data-room/${token}/files/${fileId}?pwd=${encodeURIComponent(password)}&email=${encodeURIComponent(email)}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = name || 'file';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handlePreview = (fileId) => {
    const url = `${API}/public/data-room/${token}/files/${fileId}/preview?pwd=${encodeURIComponent(password)}&email=${encodeURIComponent(email)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleZipDownload = () => {
    const url = `${API}/public/data-room/${token}/zip?pwd=${encodeURIComponent(password)}&email=${encodeURIComponent(email)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 text-amber-700 mb-1">
            <FolderLock className="w-5 h-5" />
            <span className="text-xs uppercase tracking-wider font-semibold">Confidential Data Room</span>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">{biotech_name}</h1>
          {asset_name && (
            <div className="text-sm text-slate-600 mt-1">
              Asset focus: <span className="font-medium">{asset_name}</span>
            </div>
          )}
          <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
            <div className="text-sm text-slate-500">
              Shared with <span className="font-medium text-slate-800">{recipient_pharma_name}</span>
              {' · '}
              <Badge variant="outline" className="ml-1">
                {files.length} file{files.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            <Button
              onClick={handleZipDownload}
              variant="outline"
              size="sm"
              data-testid="public-dr-zip-btn"
              disabled={!canDownloadZip}
              className={!canDownloadZip ? 'opacity-40 cursor-not-allowed' : ''}
              title={!canDownloadZip ? 'Bulk download is disabled for this share' : 'Download all files as ZIP'}
            >
              <PackageOpen className="w-3 h-3 mr-1" /> Download all as ZIP
            </Button>
          </div>
          {showDownloadDisabledNotice && (
            <div className="mt-3 text-[12px] text-blue-700 bg-blue-50 border border-blue-100 rounded px-2 py-1.5 flex items-start gap-1.5">
              <Eye className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Downloads are disabled for this share — files can only be previewed in-browser.</span>
            </div>
          )}
        </div>

        {/* Files grouped */}
        {files.length === 0 ? (
          <div className="bg-white border rounded-xl p-10 text-center text-slate-400">
            No files available in this Data Room yet.
          </div>
        ) : (
          <div className="space-y-5">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat} className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3">
                  {cat} <span className="text-slate-400">({items.length})</span>
                </div>
                <div className="space-y-1">
                  {items.map((f) => (
                    <div
                      key={f.id}
                      className="flex items-center justify-between gap-2 px-3 py-2.5 rounded hover:bg-slate-50 border border-transparent hover:border-slate-200"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm text-slate-900 truncate">{f.original_filename}</div>
                          <div className="text-[11px] text-slate-500">{fmtBytes(f.size)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePreview(f.id)}
                          data-testid={`public-dr-preview-${f.id}`}
                          title="Preview in browser"
                        >
                          <Eye className="w-3 h-3 mr-1" /> View
                        </Button>
                        {canDownloadIndividual && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownload(f.id, f.original_filename)}
                            data-testid={`public-dr-download-${f.id}`}
                          >
                            <Download className="w-3 h-3 mr-1" /> Download
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-[11px] text-slate-400 mt-8 flex items-center justify-center gap-1">
          <Shield className="w-3 h-3" />
          All access is logged · Confidential & subject to the executed CDA
        </div>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">{children}</div>
  );
}
